const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isDateString(value: string): boolean {
  if (!DATE_PATTERN.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function assertDateString(value: string): void {
  if (!isDateString(value)) throw new RangeError(`Invalid date: ${value}`);
}

export function isWeekend(date: string): boolean {
  assertDateString(date);
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

export function isBusinessDay(date: string, holidays: ReadonlySet<string> = new Set()): boolean {
  return !isWeekend(date) && !holidays.has(date);
}

function shiftDate(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

/** Add N business days, skipping weekends and configured holidays. */
export function addBusinessDays(
  startDate: string,
  days: number,
  holidays: ReadonlySet<string> = new Set(),
): string {
  assertDateString(startDate);
  if (!Number.isInteger(days) || days < 0)
    throw new RangeError("days must be a non-negative integer");
  let current = startDate;
  let remaining = days;
  while (remaining > 0) {
    current = shiftDate(current, 1);
    if (isBusinessDay(current, holidays)) remaining -= 1;
  }
  return current;
}

export function businessDaysBetween(
  startDate: string,
  endDate: string,
  holidays: ReadonlySet<string> = new Set(),
): number {
  assertDateString(startDate);
  assertDateString(endDate);
  if (endDate < startDate) return -businessDaysBetween(endDate, startDate, holidays);
  let current = startDate;
  let count = 0;
  while (current < endDate) {
    current = shiftDate(current, 1);
    if (isBusinessDay(current, holidays)) count += 1;
  }
  return count;
}

const SHANGHAI_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Returns YYYY-MM-DD in the business timezone. */
export function shanghaiDateString(date: Date): string {
  return SHANGHAI_DATE.format(date);
}

/**
 * Confirmation deadline: N business days after the start, at 23:59:59.999
 * Asia/Shanghai. Shanghai has no DST, so a fixed +08:00 offset is safe.
 */
export function confirmationDeadline(
  start: Date,
  businessDays = 3,
  holidays: ReadonlySet<string> = new Set(),
): string {
  const startDate = shanghaiDateString(start);
  const dueDate = addBusinessDays(startDate, businessDays, holidays);
  return new Date(`${dueDate}T23:59:59.999+08:00`).toISOString();
}
